// navigation.ts
// Fuente única de secciones del dashboard (icono + label + path): la usan Sidebar.vue
// y useBottomNavItems.ts para no duplicar el mapeo ruta → icono/label.
import type { FunctionalComponent } from 'vue'
import {
  HomeIcon,
  CalendarDaysIcon,
  ListBulletIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ChartBarIcon,
  LifebuoyIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  UserIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  TagIcon
} from '@heroicons/vue/24/outline'

export type NavSectionId =
  | 'dashboard'
  | 'calendar'
  | 'citas'
  | 'clients'
  | 'services'
  | 'service-orders'
  | 'cobros'
  | 'reports'
  | 'support'
  | 'whatsapp'
  | 'notifications'
  | 'profile'
  | 'companies'
  | 'subscription'

export interface NavSubItem {
  id: string
  label: string
  icon: FunctionalComponent
  path: string
  /** Activo solo con la ruta exacta (sus hijos tienen su propio ítem). */
  exact?: boolean
  /** Permiso requerido para mostrarlo (ver utils/permissions). */
  permission?: string
}

export interface NavSection {
  id: NavSectionId
  label: string
  icon: FunctionalComponent
  path: string
  /** Oculto del menú visible (Sidebar), pero la ruta sigue activa. */
  hidden?: boolean
  /** Redundante en desktop ancho (ver Sidebar): se oculta desde 1040px. */
  hideFrom1040?: boolean
  /** Solo visible si el feature de plataforma está activo (ver PlatformFeaturesService). */
  feature?: 'whatsapp'
  hasSubmenu?: boolean
  submenu?: NavSubItem[]
}

// Orden = orden visual en el Sidebar.
export const MENU_SECTIONS: NavSection[] = [
  { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, path: '/dashboard' },
  { id: 'calendar', label: 'Calendario', icon: CalendarDaysIcon, path: '/dashboard/calendar' },
  { id: 'citas', label: 'Citas', icon: ListBulletIcon, path: '/dashboard/citas' },
  { id: 'clients', label: 'Clientes', icon: UsersIcon, path: '/dashboard/clients' },
  {
    id: 'services',
    label: 'Servicios',
    icon: WrenchScrewdriverIcon,
    path: '/dashboard/services',
    hasSubmenu: true,
    submenu: [
      { id: 'services-list', label: 'Servicios', icon: WrenchScrewdriverIcon, path: '/dashboard/services', exact: true },
      { id: 'promotions', label: 'Promociones', icon: TagIcon, path: '/dashboard/services/promotions', permission: 'promotions-view' }
    ]
  },
  { id: 'service-orders', label: 'Pipeline de servicios', icon: ClipboardDocumentListIcon, path: '/dashboard/service-orders' },
  { id: 'cobros', label: 'Cobros', icon: BanknotesIcon, path: '/dashboard/cobros' },
  { id: 'reports', label: 'Reportes', icon: ChartBarIcon, path: '/dashboard/reports' },
  { id: 'support', label: 'Soporte', icon: LifebuoyIcon, path: '/dashboard/support' },
  { id: 'whatsapp', label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, path: '/dashboard/whatsapp', feature: 'whatsapp' },
  { id: 'notifications', label: 'Notificaciones', icon: BellIcon, path: '/dashboard/notifications', hidden: true }
]

// Sección "Configuration" del Sidebar (título + línea + lista aparte del menú principal).
export const CONFIGURATION_SECTIONS: NavSection[] = [
  { id: 'profile', label: 'Perfil Personal', icon: UserIcon, path: '/dashboard/settings/profile', hidden: true },
  { id: 'companies', label: 'Múltiples Empresas', icon: BuildingOfficeIcon, path: '/dashboard/settings/companies' },
  { id: 'subscription', label: 'Suscripción', icon: CreditCardIcon, path: '/dashboard/settings/subscription' }
]

export const ALL_SECTIONS: NavSection[] = [...MENU_SECTIONS, ...CONFIGURATION_SECTIONS]

/** Ids fijos del bottom nav mobile, en su orden de aparición (sin contar el FAB). */
export const BOTTOM_NAV_FIXED_IDS: NavSectionId[] = ['clients', 'calendar', 'citas', 'dashboard']

// 'dashboard' matchea exacto (si no, "/dashboard/lo-que-sea" quedaría marcado como Inicio).
const matchesSection = (path: string, section: NavSection): boolean =>
  section.id === 'dashboard'
    ? path === section.path || path === `${section.path}/`
    : path === section.path || path.startsWith(`${section.path}/`)

/** Resuelve la sección activa para una ruta por el prefijo de path más largo que matchea. */
export function resolveNavSection(path: string): NavSection | null {
  let best: NavSection | null = null
  for (const section of ALL_SECTIONS) {
    if (matchesSection(path, section) && (!best || section.path.length > best.path.length)) {
      best = section
    }
  }
  return best
}
